import { useEffect } from 'react';
import { Request } from 'express';
import debounce from 'lodash/debounce';

import { Article } from 'editorial/components/Article';
import { IArticle } from 'editorial/types';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useMParticleContext } from 'tracking/hooks/useMParticleContext';
import { cmsRequest } from 'server/services/cms-service/cmsRequest';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';
import { LOCALES, OMITTED_EDITORIAL_LOCALES } from 'localization/constants/configEnums';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';

export const EditorialArticle = ({ article, nextArticle }) => {
  const { isMParticleReady } = useMParticleContext();
  const isConsumerSearchEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  useEffect(() => {
    if (isMParticleReady) {
      sendTrackingEvent('ARTICLE_VIEW', {
        service_name: isConsumerSearchEnabled ? 'consumer_search' : 'constructor',
      });
    }
  }, [isMParticleReady]);

  const handleScroll = debounce(() => {
    sendTrackingEvent('ARTICLE_USER_SCROLL', {
      service_name: isConsumerSearchEnabled ? 'consumer_search' : 'constructor',
    });
  }, 50);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return <Article article={article} nextArticle={nextArticle} />;
};

export const getServerSideProps = async (ctx) => {
  const {
    req,
    query: { slug },
    locale,
  }: {
    req: Request;
    query: { slug: string };
    locale: string;
  } = ctx;

  // TEMPORARY REDIRECTS until we have a proper redirect system in place from the CMS
  const redirectSlugs = [
    '2020-in-sneakers-by-goat',
    'announcing-goat-as-art-basel-s-official-global-uniform-partner',
    'division-street-and-goat-partner-to-auction-exclusive-tinker-hatfield-designed-air-jordan-viii-university-of-oregon-pes-to-benefit-ducks-student-athletes',
    'division-street-and-goat-partner-to-drop-tinker-hatfield-s-first-ever-nike-dunk-low-university-of-oregon-pes-to-support-ducks-student-athletes',
    'division-street-s-ducks-of-a-feather-brand-teams-up-with-goat-to-offer-exclusive-nike-air-force-1-uo-premium-player-edition',
    'division-street-s-ducks-of-a-feather-partners-with-goat-on-new-nike-dunk-low-triple-black-luxe-what-the-duck-alternate',
    'division-street-teams-up-with-goat-and-flight-club-to-release-nike-dunk-low-what-the-duck-in-two-unique-colorways',
    'flight-club-becomes-exclusive-partner-of-crossroads-school-s-men-s-varsity-basketball-team',
    'flight-club-celebrates-the-fifth-year-of-its-flight-club-fridays-music-series',
    'flight-club-debuts-pop-up-retail-experience-in-miami',
    'flight-club-opens-pop-up-retail-experience-in-chicago',
    'flight-club-returns-to-fairfax-avenue-in-los-angeles',
    'flight-club-returns-with-its-third-installment-of-flight-club-fridays',
    'flight-club-to-host-second-annual-flight-club-invitational-at-crossroads-school-sports-center',
    'global-mexican-music-artist-peso-pluma-pro-skater-tyshawn-jones-and-fashion-pioneer-yoon-ahn-debut-as-cover-stars-on-the-ninth-issue-of-greatest-magazine',
    'goat-amazon-future-engineer-and-projectstem-recognize-student-winners-of-multi-day-computer-science-experience',
    'goat-and-bergdorf-goodman-partner-on-exclusive-shopping-experience',
    'goat-and-flight-club-merge-to-become-the-worlds-largest-sneaker-marketplace',
    'goat-and-noah-partner-to-launch-a-limited-edition-capsule-collection-mountain-goat-available-exclusively-on-goat',
    'goat-and-paris-saint-germain-announce-people-of-paris-visual-campaign-and-new-digital-experience',
    'goat-and-paris-saint-germain-announce-the-second-edition-of-people-of-paris-campaign',
    'goat-announces-lakers-kyle-kuzma-as-brand-ambassador',
    'goat-group-leads-60m-series-b-funding-in-grailed',
    'goat-group-raises-100-million-to-further-accelerate-growth-across-product-categories-and-geographies',
    'goat-group-releases-first-annual-alias-seller-report',
    'goat-group-releases-its-second-annual-alias-seller-report',
    'goat-group-valuation-more-than-doubles-to-3-7-billion-after-closing-series-f-funding-round-of-195-million',
    'goat-invites-you-to-explore-products-across-time-during-black-friday-auctions',
    'goat-launches-annual-black-friday-event',
    'goat-launches-ar-try-on-of-rare-sneakers',
    'goat-launches-bf23-event-featuring-globally-themed-fashion-experience',
    'goat-launches-sneaker-industrys-first-black-friday-augmented-reality-experience',
    'goat-michael-b-jordan-exclusive-sneaker-raffle',
    'goat-pays-tribute-to-sneaker-culture-with-pop-up-gallery-in-los-angeles',
    'goat-returns-as-art-basel-s-official-uniform-partner',
    'goat-returns-with-spring-exhibit-a-curated-shopping-experience-for-exclusive-vintage-and-archival-products',
    'goat-s-annual-black-friday-event-returns-with-bf24',
    'goat-s-greatest-black-friday-event-to-give-away-a-rare-1985-ferrari-testarossa',
    'goat-to-become-paris-saint-germain-s-sleeve-partner',
    'goat-to-launch-its-greatest-black-friday-event',
    'goatdiy-returns-for-its-third-annual-global-contest',
    'goats-global-expansion-begins-with-china',
    'greatest-magazine-returns-with-its-eighth-issue-featuring-iconic-music-duo-clipse-and-erl-designer-eli-russell-linnetz',
    'greatest-magazine-returns-with-its-seventh-issue-featuring-musician-brent-faiyaz-and-designer-mowalola',
    'groupe-artemis-makes-strategic-investment-in-goat-group',
    'manifesto-xxiii',
    'sneaker-marketplace-goat-announces-black-friday-drawing',
  ];

  if (redirectSlugs.includes(slug)) {
    return {
      redirect: {
        destination: `https://www.goatgroup.com/news/${slug}`,
        statusCode: 308,
      },
    };
  }

  let nextArticle = null;

  if (OMITTED_EDITORIAL_LOCALES.includes(locale as LOCALES)) {
    return { notFound: true };
  }

  try {
    const data = cmsRequest(req).get(`api/v1/cms/cms_articles/${slug}`);
    const article: IArticle = await data.json();

    if (article?.category) {
      const nextArticleData = cmsRequest(req).get(
        `api/v1/cms/cms_articles/${slug}/next?category=${article.category}`,
      );
      nextArticle = await nextArticleData.json();
    }

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        article,
        nextArticle,
      },
    };
  } catch {
    return {
      redirect: {
        destination: getRedirectionPath('/editorial', locale),
        permanent: false,
      },
    };
  }
};

export default EditorialArticle;
